// Sınırsız (unlimited) bundle detayı — /esim/unlimited/:bundleId
//
// Eski katalogdan iki yapısal farkı var:
//  1. /v2/bundles tekil bundle ucu sunmaz; liste önbellekten okunup id ile
//     bulunur (bkz. unlimitedPlans.js).
//  2. Ayrı bir plan detay ucu (/bundle/{id}/{refillId} karşılığı) yok, bu yüzden
//     satın alma bu sayfadan doğrudan checkout'a gider.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faInfinity,
  faClock,
  faArrowRight,
  faEarthAmericas,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import AuthPromptModal from '../../components/AuthPromptModal';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPrice, pickPlanPrice } from '../../utils/currency';
import { formatValidity } from '../../utils/formatters';
import {
  loadUnlimitedBundles,
  findUnlimitedBundle,
  sellablePlans,
  isSellable,
} from './unlimitedPlans';
import './home.css';

const Feature = ({ icon, label, value }) => (
  <div className="feature">
    <FontAwesomeIcon icon={icon} className="feature-icon" />
    <div>
      <span className="feature-label">{label}</span>
      <span className="feature-value">{value}</span>
    </div>
  </div>
);

const UnlimitedDetail = () => {
  const { t, i18n } = useTranslation();
  const { bundleId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currency } = useCurrency();

  const [state, setState] = useState({ loading: true });
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    loadUnlimitedBundles()
      .then((list) => {
        if (cancelled) return;
        const bundle = findUnlimitedBundle(list, bundleId);
        setState(bundle ? { bundle } : { error: t('detail.empty') });
      })
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
    // Dil değişince katalog yeniden çekilir (ülke adları Accept-Language'e bağlı).
  }, [bundleId, i18n.language, t]);

  const bundle = state.bundle;
  const plans = sellablePlans(bundle);
  const countries = bundle?.countries || [];
  const countryNames = countries.map((c) => c.nameSelected || c.name).filter(Boolean);

  const buy = (plan) => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    navigate('/checkout', {
      state: {
        // plan.id hem ürün kimliği hem SKU'dur; backend `productId` olarak bunu
        // bekler. priceId fiyat kilidi — zorunlu.
        productId: String(plan.id),
        priceId: plan.priceId,
        plan: {
          countryName: bundle.title,
          dataAmount: t('detail.unlimited'),
          validity: String(plan.validityDays),
          flag: bundle.imgUrl,
          isUnlimited: true,
          // Tahsil edilen gerçek tutar versionedPriceEur'dur (PayPal, EUR).
          priceEuro: plan.versionedPriceEur || plan.priceEur,
        },
      },
    });
  };

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog detail">
        <Link to="/" className="esim-detail-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </Link>

        <header className="detail-head">
          {bundle?.imgUrl ? (
            <img className="detail-flag" src={bundle.imgUrl} alt="" />
          ) : (
            <span className="detail-flag detail-flag--fallback">
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
          <div>
            <h1 className="detail-title">
              {bundle?.title || t('home.tabs.unlimited')}
              {bundle && <span className="detail-badge">{t('detail.unlimited')}</span>}
            </h1>
            {plans.length > 0 && (
              <p className="detail-sub">{t('detail.count', { count: plans.length })}</p>
            )}
          </div>
        </header>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{state.error}</p>
          </div>
        )}

        {bundle && (
          <>
            <p className="detail-notice">{t('detail.unlimitedNote')}</p>

            {plans.length === 0 ? (
              <div className="home-state">
                <p>{t('detail.empty')}</p>
              </div>
            ) : (
              <ul className="plan-list">
                {plans.map((plan) => {
                  const price = formatPrice(pickPlanPrice(plan, currency), currency);
                  const sellable = isSellable(plan);
                  return (
                    <li key={plan.id}>
                      <button
                        type="button"
                        className={`plan plan--button ${sellable ? '' : 'plan--disabled'}`}
                        disabled={!sellable}
                        onClick={() => buy(plan)}
                      >
                        <span className="plan-data">
                          <FontAwesomeIcon icon={faInfinity} />
                          {t('detail.unlimited')}
                        </span>
                        <span className="plan-validity">
                          <FontAwesomeIcon icon={faClock} />
                          {formatValidity(t, plan.validityDays)}
                        </span>
                        {price && <span className="plan-price">{price}</span>}
                        {sellable ? (
                          <span className="plan-buy">
                            {t('detail.buy')}
                            <FontAwesomeIcon icon={faArrowRight} />
                          </span>
                        ) : (
                          // priceId yoksa plan fiyatlanmamıştır; web'den satılamaz.
                          <span className="plan-unavailable">{t('detail.notPriced')}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <h2 className="plan-section-title">{t('detail.basicFeatures')}</h2>
            <div className="plan-features">
              <Feature
                icon={faInfinity}
                label={t('detail.dataAmount')}
                value={t('detail.unlimited')}
              />
              <Feature
                icon={faBolt}
                label={t('detail.activationMethod')}
                value={t('detail.esimActivation')}
              />
              {countryNames.length > 0 && (
                <Feature
                  icon={faEarthAmericas}
                  label={t('detail.coverageArea')}
                  value={countryNames.join(', ')}
                />
              )}
            </div>

            {bundle.description && (
              <>
                <h2 className="plan-section-title">{t('detail.description')}</h2>
                <p className="plan-description">{bundle.description}</p>
              </>
            )}
          </>
        )}
      </section>

      <Footer />

      <AuthPromptModal open={authPromptOpen} onClose={() => setAuthPromptOpen(false)} />
    </div>
  );
};

export default UnlimitedDetail;
